export function parseContext(value) {
    if (!value || typeof value !== 'string') {
        return null;
    }
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
}

/** Tekst do osadzenia w łańcuchu przekazywanym do dangerouslySetInnerHTML (nie surowy markup). */
export function escapeHtmlText(s) {
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * Tooltip z surowym HTML.
 * Używa `window.wp.element` (to samo API co viewer wtyczki — zależność `wp-element`).
 *
 * @param {string} html — ufaj całości; dynamiczny tekst z API escapuj (np. `escapeHtmlText`) zanim złożysz łańcuch
 * @returns {import('react').ReactElement | null}
 */
export function tooltipFromTrustedHtml(html) {
    const createElement = window.wp?.element?.createElement;
    if (typeof createElement !== 'function') {
        console.warn(
            '[estate-map] Brak window.wp.element — włącz skrypt wtyczki MDA Estate Map (ładuje wp-element).'
        );
        return null;
    }
    return createElement('div', {
        className: 'mda-estate-map__tooltip-html',
        dangerouslySetInnerHTML: { __html: html },
    });
}

export class MapEstateViewer {
    /**
     * Instancja `window.MdaEstateMapViewer` po udanym mount; `null` gdy pominięto lub brak viewer.
     * @type {InstanceType<NonNullable<typeof window.MdaEstateMapViewer>> | null}
     */
    viewer = null;

    /** @type {Promise<InstanceType<NonNullable<typeof window.MdaEstateMapViewer>> | null>} */
    ready;

    _pendingContext = null;

    /**
     * @param {HTMLElement} mapEl
     */
    constructor(mapEl) {
        this.mapEl = mapEl;
        this.ready = this._loadAndMount();
    }

    _loadAndMount() {
        if (window.MdaEstateMapViewer) {
            return Promise.resolve(this._mount(this.mapEl));
        }

        return new Promise(resolve => {
            const mount = () => resolve(this._mount(this.mapEl));
            window.addEventListener('mda-estate-map-assets-ready', mount, {
                once: true,
            });

            // The lazy loader can finish between the first check and listener setup.
            if (window.MdaEstateMapViewer) {
                window.removeEventListener(
                    'mda-estate-map-assets-ready',
                    mount
                );
                mount();
            }
        });
    }

    refreshContext(contextData) {
        this._pendingContext = contextData;
        if (this.viewer) {
            this.viewer.refreshContext(contextData);
            return;
        }

        this.ready.then(viewer => viewer?.refreshContext(this._pendingContext));
    }

    /**
     * @param {HTMLElement} mapEl
     * @returns {InstanceType<NonNullable<typeof window.MdaEstateMapViewer>> | null}
     */
    _mount(mapEl) {
        if (mapEl.dataset.mdaEstateMapMounted) {
            return null;
        }

        const Viewer = window.MdaEstateMapViewer;
        if (!Viewer) {
            console.warn(
                '[estate-map] MdaEstateMapViewer niedostępny — włącz / zainstaluj wtyczkę MDA Estate Map.'
            );
            return null;
        }

        const scenario = mapEl.dataset.scenario || '';
        const node = mapEl.dataset.node || '';

        if (!scenario) {
            console.warn(
                '[estate-map] Ustaw atrybut data-scenario na sekcji lub na kontenerze mapy (slug lub id scenariusza).'
            );
            return null;
        }

        mapEl.dataset.mdaEstateMapMounted = '1';
        mapEl.classList.add('mda-estate-map');

        const contextAttr = mapEl.dataset.context || null;
        const contextData = parseContext(contextAttr);

        const options = {
            scenario,
            node: node || undefined,
        };
        if (contextData !== null) {
            options.contextData = contextData;
        }

        // options.getTooltipProps = (shape, meta, viewerContext) => ({ label: meta.label || '' });

        // ctx: { shape, meta, contextData, defaultProps, zone } — zone = { shape, meta } aktualnej strefy
        options.renderTooltip = ({ defaultProps, contextData, zone }) => {
            const zoneId = zone.shape?.id != null && zone.shape.id !== '';
            const identityMode = zone.meta.identity_mode;
            const targetPostType =
                zone.shape.target_post_type ??
                zone.shape.target_post?.type ??
                '';
            const targetPost = zone.shape.target_post ?? null;
            const apartments = contextData?.apartments ?? [];

            if (identityMode === 'building') {
                const buildingSymbol = escapeHtmlText(
                    zone.meta.building_symbol || ''
                );

                return tooltipFromTrustedHtml(
                    `<div class="p-16 rounded-16 bg-white">
                    <strong>Budynek ${escapeHtmlText(
                        buildingSymbol || ''
                    )}</strong>
                </div>`
                );
            }

            if (targetPostType === 'apartment') {
                const apartment = apartments.find(
                    apartment => apartment.id === targetPost.id
                );
                if (!apartment) return null;

                const { url, title, status, surface, floor, rooms } = apartment;

                return tooltipFromTrustedHtml(
                    `<div class="relative mda-estate-map__tooltip mda-estate-map__tooltip-apartment p-16 rounded-[16px] w-[232px] bg-white">
                    ${
                        url
                            ? `<a href="${url}" target="_blank" class="absolute top-0 left-0 w-full h-full"></a>`
                            : ''
                    }
                    <div class="flex justify-between items-center gap-2 mb-8">
                        <div class="h5 !font-secondary font-bold">${escapeHtmlText(
                            title || ''
                        )}</div>
                        ${
                            status
                                ? `<div class="mda-estate-map__tooltip-status -${
                                      status.value
                                  }">${escapeHtmlText(
                                      status.label || ''
                                  )}</div>`
                                : ''
                        }
                    </div>
                    <div class="flex gap-x-24 gap-y-16">
                        ${
                            surface
                                ? `<div class="flex items-center gap-8">
                            <div class="flex icon icon-arrow-expand text-gray"></div>
                            <div class="text-regular-fluid text-gray">${escapeHtmlText(
                                surface || ''
                            )} m<sup>2</sup></div>
                        </div>`
                                : ''
                        }
                        ${
                            floor
                                ? `<div class="flex items-center gap-8">
                            <div class="flex icon icon-lift-outline text-gray"></div>
                            <div class="text-regular-fluid text-gray">${escapeHtmlText(
                                floor || ''
                            )}</div>
                        </div>`
                                : ''
                        }
                        ${
                            rooms
                                ? `<div class="flex items-center gap-8">
                            <div class="flex icon icon-floor text-gray"></div>
                            <div class="text-regular-fluid text-gray">${escapeHtmlText(
                                rooms || ''
                            )}</div>
                        </div>`
                                : ''
                        }
                    </div>
                </div>`
                );
            }

            // Można dodać tekst tooltip do innych trybów identyfikacji (np. 'floor', 'room', 'unit', 'zone').
            return tooltipFromTrustedHtml(
                `<div class="p-16 rounded-16 bg-white"${zoneId}></div>`
            );
        };

        // Ukrywanie stref względem contextData z data-context, np. {"postIds":[1,2,3]}:
        options.renderZone = ({ shape, meta, contextData: ctx }) => {
            const postId = shape.target_post?.id;
            const postType =
                shape.target_post_type ?? shape.target_post?.type ?? '';

            if (postId === null || typeof postId === 'undefined') return null;

            if (postType === 'apartment') {
                const apartmentsIds =
                    ctx.apartments?.map(apartment => apartment.id) ?? [];
                return apartmentsIds.includes(Number(postId)) ? null : false;
            }

            return null;
        };

        options.setZoneClass = ({ shape, meta, contextData: ctx }) => {
            let className = '';

            const identityMode = meta.identity_mode;
            const targetPostType =
                shape.target_post_type ?? shape.target_post?.type ?? '';
            const targetPost = shape.target_post ?? null;
            const apartments = ctx?.apartments ?? [];

            if (identityMode === 'building') {
                return '-building';
            }

            if (targetPostType === 'apartment') {
                const apartment = apartments.find(
                    apartment => apartment.id === targetPost.id
                );
                if (!apartment) return null;

                const { status } = apartment;
                if (status) className = `-${status.value}`;
            }

            return className;
        };

        this.viewer = new Viewer(mapEl, options);
        if (this._pendingContext !== null) {
            this.viewer.refreshContext(this._pendingContext);
        }
        return this.viewer;
    }
}
